// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, PromotionModel } from "@insite/client-framework/Types/ApiModels";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import CartLineNotes, { CartLineNotesStyles } from "@insite/content-library/Widgets/Cart/CartLineNotes";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import CartLineContainer, { CartLineContainerStyles } from "@pace/App/Components/Cart/CartLineContainer";
import Notify from "@pace/App/Components/Notify";
import cartLineSharedStyles from "@pace/App/SharedStyles/CartLineSharedStyles";
import ProductAvailability from "@pace/Overrides/Components/ProductAvailability";
import ProductPrice, { ProductPriceStyles } from "@pace/Overrides/Components/ProductPrice";
import CartLineQuantity, { CartLineQuantityStyles } from "@pace/Overrides/Widgets/Cart/CartLineQuantity";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    cart: Cart;
    promotions?: PromotionModel[];
    showSavingsAmount: boolean;
    showSavingsPercent: boolean;
    editable: boolean;
    showInvalidPriceMessage?: boolean;
    extendedStyles?: CartLineCardExpandedStyles;
}

const mapStateToProps = (state: ApplicationState) => ({
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    showPricing: state.context.showPricing,
    isCheckoutReviewAndSubmitPage: getCurrentPage(state).type === "CheckoutReviewAndSubmitPage",
});

type Props = OwnProps & HasCartLineContext & ReturnType<typeof mapStateToProps>;

export interface CartLineCardExpandedStyles {
    cartLineNotes?: CartLineNotesStyles;
    price?: ProductPriceStyles;
    quantity?: CartLineQuantityStyles;
    container?: GridContainerProps;
    productImageGridItem?: GridItemProps;
    productImage?: ProductImageStyles;
    orderLineInfoGridItem?: GridItemProps;
    orderLineInfoContainer?: GridContainerProps;
    productDescriptionAndPartNumbersGridItem?: GridItemProps;
    productDescriptionAndPartNumbersContainer?: GridContainerProps;
    productBrand?: ProductBrandStyles;
    productBrandAndDescriptionGridItem?: GridItemProps;
    productDescription?: ProductDescriptionStyles;
    configurationGridItem?: GridItemProps;
    configurationOptionText?: TypographyProps;
    productPartNumbersGridItem?: GridItemProps;
    productPartNumbers?: ProductPartNumbersStyles;
    orderLineNotesGridItem?: GridItemProps;
    notesHeadingAndText?: SmallHeadingAndTextStyles;
    costCodeGridItem?: GridItemProps;
    costCodeValueText?: TypographyPresentationProps;
    costCodeLabelText?: TypographyPresentationProps;
    productPriceAndQuantityGridItem?: GridItemProps;
    productPriceAndQuantityContainer?: GridContainerProps;
    priceGridItem?: GridItemProps;
    priceHeadingAndText?: SmallHeadingAndTextStyles;
    promotionNameText?: TypographyProps;
    quantityGridItem?: GridItemProps;
    qtyHeadingAndText?: SmallHeadingAndTextStyles;
    extendedUnitNetPriceGridItem?: GridItemProps;
    extendedUnitNetPrice?: SmallHeadingAndTextStyles;
    subtotalWithoutVatText?: SmallHeadingAndTextStyles;
    vatAmount?: SmallHeadingAndTextStyles;
    cartLineErrorMessageGridItem?: GridItemProps;
    cartLineErrorMessageText?: TypographyProps;
    // XNG-Changes
    cartLineContainer?: CartLineContainerStyles;
    nonStockInventoryMessage?: TypographyPresentationProps;
}

export const cartLineCardExpandedStyles: CartLineCardExpandedStyles = {
    container: {
        gap: 20,
        css: css`
            border-bottom: 1px solid ${getColor("common.border")};
            padding: 1rem 0;
        `,
    },
    productImageGridItem: {
        width: 2,
        printWidth: 1,
        css: css`
            @media print {
                max-height: 83px;
                max-width: 83px;
                padding: 0;
            }
        `,
    },
    orderLineInfoGridItem: {
        width: 10,
        printWidth: 11,
    },
    orderLineInfoContainer: {
        gap: 15,
    },
    productDescriptionAndPartNumbersGridItem: {
        width: [12, 12, 5, 5, 5],
        printWidth: 6,
    },
    productDescriptionAndPartNumbersContainer: {
        gap: 15,
    },
    productBrand: {
        nameText: {
            color: "text.main",
        },
    },
    productBrandAndDescriptionGridItem: {
        css: css`
            flex-direction: column;
        `,
        width: 12,
    },
    configurationGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    productPartNumbersGridItem: {
        width: 12,
    },
    orderLineNotesGridItem: {
        width: 12,
    },
    costCodeGridItem: { width: 12 },
    costCodeLabelText: {
        weight: "bold",
        css: css`
            margin-right: 10px;
        `,
    },
    productPriceAndQuantityGridItem: {
        width: [12, 12, 7, 7, 7],
        printWidth: 6,
    },
    productPriceAndQuantityContainer: {
        gap: 15,
    },
    priceGridItem: {
        width: [12, 12, 5, 5, 5],
        printWidth: 5,
        css: css`
            flex-direction: column;
        `,
    },
    quantityGridItem: {
        width: [12, 12, 3, 3, 3],
    },
    extendedUnitNetPriceGridItem: {
        width: [12, 12, 4, 4, 4],
        css: css`
            flex-direction: column;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        css`
                            flex-flow: wrap;
                        `,
                    ],
                    "max",
                )}
        `,
    },
    extendedUnitNetPrice: {
        text: {
            weight: 700,
        },
    },
    subtotalWithoutVatText: {
        wrapper: {
            css: css`
                margin-bottom: 6px;
                width: 100%;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                width: 50%;
                            `,
                        ],
                        "max",
                    )}
            `,
        },
    },
    vatAmount: {
        wrapper: {
            css: css`
                margin-bottom: 6px;
                width: 100%;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            css`
                                width: 50%;
                            `,
                        ],
                        "max",
                    )}
            `,
        },
    },
    cartLineErrorMessageGridItem: {
        width: 12,
    },
    cartLineErrorMessageText: {
        color: "danger",
    },
};

const CartLineCardExpanded = ({
    cart,
    cartLine,
    showSavingsAmount,
    showSavingsPercent,
    promotions,
    editable,
    enableVat,
    vatPriceDisplay,
    showInvalidPriceMessage,
    showPricing,
    isCheckoutReviewAndSubmitPage,
    extendedStyles,
}: Props) => {
    const [styles] = React.useState(() => mergeToNew(cartLineCardExpandedStyles, extendedStyles));
    // <XNG-Changes>
    const errorMessages: React.ReactNode[] = [];
    if (cartLine.isRestricted) {
        errorMessages.push(translate("Restricted product"));
    }
    if (!cartLine.isActive) {
        errorMessages.push(translate("Inactive Product"));
    }
    const isNonStockProduct = (product: CartLineModel) => {
        return product.altText === "non-stock" || !product.productUri;
    };
    // <XNG-Changes>
    
    return (
        <CartLineContainer extendedStyles={styles.cartLineContainer}>
            <CartLineContainer.Image>
                <ProductImage product={cartLine} extendedStyles={styles.productImage} />
            </CartLineContainer.Image>
            <CartLineContainer.Description>
                <StyledWrapper className="brand-description-wrapper">
                    {cartLine.brand && <ProductBrand brand={cartLine.brand} extendedStyles={styles.productBrand} />}
                    <ProductDescription product={cartLine} extendedStyles={styles.productDescription} />
                </StyledWrapper>
                <ProductPartNumbers
                    productNumber={cartLine.erpNumber}
                    customerProductNumber={cartLine.customerName}
                    manufacturerItem={cartLine.manufacturerItem}
                    extendedStyles={styles.productPartNumbers}
                />
            </CartLineContainer.Description>
            <CartLineContainer.Availability>
                {!cartLine.quoteRequired && !isNonStockProduct(cartLine) && (
                    <ProductAvailability
                        productId={cartLine.productId!}
                        availability={cartLine.availability!}
                        unitOfMeasure={cartLine.unitOfMeasure}
                        trackInventory={cartLine.trackInventory}
                        availabilityResult={{
                            qtyInCustomerBranch: cartLine.properties["qtyInCustomerBranch"],
                            qtyInDc: cartLine.properties["qtyInDc"],
                            qtyInNetwork: cartLine.properties["qtyInNetwork"],
                        }}
                        failedToLoadInventory={cart.failedToGetRealTimeInventory}
                        extendedStyles={cartLineSharedStyles.productAvailability}
                    />
                )}
                {/* Xng-Change: PAC-1142 display site message for non-stock items availability */}
                {!cartLine.productUri && (
                    <Typography {...styles.nonStockInventoryMessage}>{siteMessage("Custom_RealtimeInventoryMessageForNonStockItems")}</Typography>
                )}
                {/* TODO: add important info component */}
            </CartLineContainer.Availability>
            <CartLineContainer.Price>
                {!cart.cartNotPriced && (
                    <ProductPrice
                        product={cartLine}
                        showSavings={true}
                        showSavingsAmount={showSavingsAmount}
                        showSavingsPercent={showSavingsPercent}
                        showInvalidPriceMessage={showInvalidPriceMessage}
                        extendedStyles={styles.price}
                        {...{ labelOverride: translate("Price/Unit") }}
                        isNonStockItem={isNonStockProduct(cartLine)}
                    />
                )}
            </CartLineContainer.Price>
            <CartLineContainer.Qty>
                <CartLineQuantity cart={cart} editable={editable} extendedStyles={styles.quantity} />
            </CartLineContainer.Qty>
            <CartLineContainer.Total>
                {!cartLine.quoteRequired && !cart.cartNotPriced && cartLine.pricing && showPricing && (
                    <StyledWrapper {...styles.extendedUnitNetPriceGridItem}>
                        {cartLine.pricing && (showPricing || isCheckoutReviewAndSubmitPage) && (
                            <>
                                {enableVat && vatPriceDisplay === "DisplayWithAndWithoutVat" && (
                                    <>
                                        <SmallHeadingAndText
                                            heading={`${translate("Subtotal")} (${translate("Ex. VAT")})`}
                                            text={cartLine.pricing.extendedUnitNetPriceDisplay}
                                            extendedStyles={styles.subtotalWithoutVatText}
                                        />
                                        <SmallHeadingAndText
                                            heading={`${translate("Total VAT")} (${cartLine.pricing.vatRate}%)`}
                                            text={cartLine.pricing.vatAmountDisplay}
                                            extendedStyles={styles.vatAmount}
                                        />
                                    </>
                                )}
                                <SmallHeadingAndText
                                    {...styles.extendedUnitNetPrice}
                                    heading={
                                        !enableVat
                                            ? translate("Subtotal")
                                            : vatPriceDisplay !== "DisplayWithoutVat"
                                            ? `${translate("Subtotal")} (${translate("Inc. VAT")})`
                                            : `${translate("Subtotal")} (${translate("Ex. VAT")})`
                                    }
                                    text={
                                        enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                            ? cartLine.pricing.extendedUnitRegularPriceWithVatDisplay
                                            : cartLine.pricing.extendedUnitNetPriceDisplay
                                    }
                                />
                            </>
                        )}
                    </StyledWrapper>
                )}
            </CartLineContainer.Total>

            <CartLineContainer.Notes>
                {cartLine.notes && (
                    <CartLineNotes
                        cart={cart}
                        editable={editable}
                        extendedStyles={styles.cartLineNotes}
                        // XNG-Changes
                        {...{ noteFieldsVisible: true }}
                    />
                )}

                {cart.showCostCode && cartLine.costCode && (
                    <StyledWrapper {...styles.costCodeGridItem}>
                        <Typography {...styles.costCodeValueText} as="p">
                            <Typography {...styles.costCodeLabelText}>{cart.costCodeLabel}:</Typography>
                            {cartLine.costCode}
                        </Typography>
                    </StyledWrapper>
                )}
                {promotions?.map(promotion => (
                    <Typography
                        {...styles.promotionNameText}
                        key={promotion.id}
                        data-test-selector="orderLineCardExpandedPromotionName"
                    >
                        {promotion.name}
                    </Typography>
                ))}

                {!cartLine.isFixedConfiguration && cartLine.sectionOptions!.length > 0 && (
                    <StyledWrapper {...styles.configurationGridItem}>
                        {cartLine.sectionOptions!.map(option => (
                            <Typography {...styles.configurationOptionText} key={option.sectionOptionId}>
                                {`${option.sectionName}:${option.optionName}`}
                            </Typography>
                        ))}
                    </StyledWrapper>
                )}
                {errorMessages.length > 0 && (
                    <StyledWrapper {...styles.cartLineErrorMessageGridItem}>
                        {errorMessages.map((message, index) => (
                            <Notify
                                {...styles.cartLineErrorMessageText}
                                type="danger"
                                // eslint-disable-next-line react/no-array-index-key
                                key={index}
                                data-test-selector="cartline_errorMessage"
                            >
                                {message}
                            </Notify>
                        ))}
                    </StyledWrapper>
                )}
            </CartLineContainer.Notes>
        </CartLineContainer>
    );
};

export default connect(mapStateToProps)(withCartLine(CartLineCardExpanded));
